import { authState, sendMessage } from '@composables/useWindowEvents.js'
import { subscriptionStore, ratingStore, authStore } from '@stores'
import { REPLICATION_DELAY_MS, AUTH_BASE_URL } from '@config'
import * as sdkInterface from '@plugins/sdkInterface.js'
import { LOGIN_METHODS } from '@config/auth.js'
import useToast from '@composables/useToast.js'
import { asyncSleep } from '@helpers/utils.js'
import i18n from '@plugins/localisation.js'
import { myTeamsStore } from '@stores'
import qs from 'qs'
import { PUBLIC_BASE_URL, LOGOUT_ENDPOINT, GAME_ID } from '@config/game.js'
import {
  getAllUserTeamSummaryRequest,
  getMySubscriptionsRequest,
  getAllUserRatings,
  getUserRequest,
  createClient,
} from '@services'

const { clearUser, setToken, setUser, isLoggedIn } = authStore()
const { setMySubscriptions } = subscriptionStore()
const { setUserRatings } = ratingStore()
const { setMyTeams } = myTeamsStore()
const { get, post } = createClient()

/* API request to update local storage data */
export async function updateLocalStorage(user) {
  const response = await Promise.allSettled([
    user ? Promise.resolve() : getUserRequest(),
    getMySubscriptionsRequest(),
    getAllUserRatings(),
    getAllUserTeamSummaryRequest(),
  ])

  if (response.some((promise) => promise.reason?.code === 401)) {
    // unauthorised
    clearUser()
    const t = i18n.global.t
    useToast().addToast({
      title: t('login_fail'),
      isError: true,
      text: t('login_fail_message'),
    })
    throw new Error()
  }

  if (response[1].status === 'fulfilled')
    setMySubscriptions(response[1].value.data)
  if (response[2].status === 'fulfilled') setUserRatings(response[2].value.data)
  if (response[3].status === 'fulfilled') setMyTeams(response[3].value.data)

  // set user required to HERE to kept login modal visible
  // until local storage data requests complete
  if (response[0].status === 'fulfilled') {
    setUser(user || response[0].value)
  }
}

/* API request to request code */
export async function requestCodeRequest({ email } = {}) {
  const { data } = await post(
    `${AUTH_BASE_URL}/emailrequest`,
    qs.stringify({ email, game_id: GAME_ID }),
    { withCredentials: true }
  )
  return data
}

/* API request to login using code */
export async function loginRequest({ code } = {}) {
  const {
    data: { access_token },
  } = await post(
    `${AUTH_BASE_URL}/emailexchange`,
    qs.stringify({ code, game_id: GAME_ID }),
    {
      withCredentials: true,
    }
  )

  await boot({ access_token })
}

/* API request to retrieve oauth login redirect url */
export async function oAuthUrlRequest(service, gameId = null) {
  let url = _getUrl(service)
  if (!url) return

  if (gameId) {
    url += `?game_id=${gameId}`
  }

  const { data } = await get(`${AUTH_BASE_URL}${url}`, {
    withCredentials: true,
  })

  if (data.url) {
    return data.url
  }
}

/* API request code login user with oauth */
export async function oAuthLoginRequest(service, path) {
  try {
    const url = _getUrl(service)
    if (!url || !service || !path) {
      throw new Error()
    }

    const query = path.replace(/[^?]*/, '')

    const {
      data: { access_token, user },
    } = await get(`${AUTH_BASE_URL}${url}${query}&game_id=${GAME_ID}`, {
      withCredentials: true,
    })

    if (access_token) {
      return await boot({ access_token, user })
    } else {
      throw new Error()
    }
  } catch (error) {
    clearUser()
    throw error
  }
}

/* API request to get SSO Info (name/icon) */
export async function getSSOInfoRequest() {
  const { data } = await get(
    `${AUTH_BASE_URL}/studio/info?game_id=${GAME_ID}`,
    {
      withCredentials: true,
    }
  )

  return data
}

/* API request to logout */
export async function logoutRequest() {
  if (!isLoggedIn.value) return

  try {
    await post(LOGOUT_ENDPOINT)
  } catch (err) {
    console.error(err)
  } finally {
    clearUser()
  }
}

export async function boot({ access_token, user }) {
  // handle replication delay
  await asyncSleep(REPLICATION_DELAY_MS)

  try {
    clearUser()
    setToken(access_token)
    sdkInterface.setAuthToken(access_token)

    await updateLocalStorage(user)
  } catch (error) {
    console.error(error)
  }
}

export async function exchangeIDtokenForAccessToken(id_token, email) {
  const update = { id_token }
  if (email) {
    update.email = email
  }

  try {
    const {
      data: { access_token },
    } = await post(
      `${PUBLIC_BASE_URL}/external/openidauth`,
      qs.stringify(update)
    )

    await boot({ access_token })
  } catch (error) {
    console.error(error)
    authState.value.error = error
    sendMessage({ modioAuth: 'refresh' })
    useToast().addToast({
      title: i18n.global.t('login_fail'),
      isError: true,
      text: error,
    })
  }
}

function _getUrl(type) {
  const loginMethod = LOGIN_METHODS.find((method) => method.type === type)

  if (loginMethod?.url) {
    return loginMethod.url
  } else {
    throw new Error('Invalid login service')
  }
}
